import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import { EAppletAuditStatus, EAppletLatestVersion, IAppletAgreementRequest, IAppletAuthInfo, IAppletManageItem, IAppletManageRequest, IAppletPublishRequest, IAppletSubmitInfoParam, IAppletSubmitInfoRequest, IAppletSubmitInfoRes } from '@core/main/types';

const hostUrl = getHostbaseUrl();

/**
 * 格式化小程序管理数据，根据审核状态计算各操作按钮的显示状态
 * @param data 小程序管理列表数据
 * @returns 格式化后的小程序管理列表数据，包含各按钮显示标志
 */
function formatAppletManageData(data: IAppletManageItem[]) {
  return data?.map((item) => {
    // 判断是否为未绑定状态
    const isNotBind = item.auditStatus === EAppletAuditStatus.NOT_BIND;
    // 判断是否为审核驳回状态（包括微信驳回）
    const isRejected = item.auditStatus === EAppletAuditStatus.REJECT || item.auditStatus === EAppletAuditStatus.WECHAT_REJECT;
    // 判断是否为已发布且非最新版本
    const isPublishedNotLatest = item.auditStatus === EAppletAuditStatus.PUBLISHED && item.isLatestVersion === EAppletLatestVersion.No;

    return {
      ...item,
      // 送审资料按钮：非未绑定状态时显示
      showSubmitInfo: !isNotBind ? 1 : 0,
      // 已授权项按钮：非未绑定状态时显示
      showAuthorizedItem: !isNotBind ? 1 : 0,
      // 发布最新版本按钮：已发布且非最新版本时显示
      showPublishLatest: isPublishedNotLatest ? 1 : 0,
      // 体验小程序按钮：非未绑定状态时显示
      showExperience: !isNotBind ? 1 : 0,
      // 驳回原因按钮：审核驳回或微信驳回时显示
      showRejectReason: isRejected ? 1 : 0,
    };
  });
}

/**
 * 小程序管理分页
 */
export async function queryAppletManagePage(param: IAppletManageRequest) {
  let data = [] as IAppletManageItem[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IAppletManageRequest;

  try {
    const { code, context, message } = await Http.post<{ data: IAppletManageItem[]; total: number }>(`${hostUrl}/oem/applet/page`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IAppletManageItem[]; total: number };

      data = formatAppletManageData(items || []);
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 小程序管理列表
 */
export async function queryAppletManageList(auditStatus: EAppletAuditStatus) {
  let data = [] as IAppletManageItem[];

  try {
    const { code, context, message } = await Http.post<IAppletManageItem[]>(`${hostUrl}/oem/applet/list`, { auditStatus });

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as IAppletManageItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 小程序驳回 重新发布小程序
 */
export async function publishApplet(params: IAppletPublishRequest) {

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/oem/applet/pushApplet`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 小程序驳回 重新发布小程序
 */
export async function addAppletManage(agentId: number) {

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/oem/applet/add`, { agentId });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 小程序配置协议
 */
export async function updateAppletAgreement(params: IAppletAgreementRequest) {

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/oem/applet/agreement`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 小程序获取送审资料
 */
export async function queryAppletSubmitInfo(params: IAppletSubmitInfoRequest) {
  let data = {} as IAppletSubmitInfoRes;

  try {
    const { code, context, message } = await Http.post<IAppletSubmitInfoRes>(`${hostUrl}/oem/applet/getSubmitInfo`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const items = context || {} as IAppletSubmitInfoRes;

      data = items || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 小程序送审资料保存
 */
export async function saveAppletSubmitInfo(params: IAppletSubmitInfoParam) {

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/oem/applet/setSubmitInfo`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 获取小程序试用链接
 */
export async function queryAppletTrialQrcode(appletId: number) {
  let binary = undefined;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/oem/applet/trial-qrcode`, 
      { appletId }, 
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      binary = context || undefined;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return binary;
}

/**
 * 获取小程序授权信息
 */
export async function queryAppletAuthInfo(agentId: number, auth_code: string) {
  let data = {} as IAppletAuthInfo;

  try {
    const { code, context, message } = await Http.get<IAppletAuthInfo>(
      `${hostUrl}/oem/applet`, 
      {
        agentId,
        auth_code, 
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IAppletAuthInfo;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 小程序批量发布最新版本
 */
export async function publishAppletLatest(appletIdList: number[]) {
  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/oem/applet/release`, { appletIdList });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
} 